import React, { useCallback, useEffect, useMemo, useState } from "react";
import {useNavigate} from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
  Tooltip,
  Badge
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EventNoteIcon from "@mui/icons-material/EventNote";
import NotesIcon from "@mui/icons-material/Notes";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PhoneIcon from "@mui/icons-material/Phone";
import PrintIcon from "@mui/icons-material/Print";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import ApiCallService from "../../../Service/ApiCallService";
import dummyImage from "../../../images/user.png";
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import HistoryIcon from '@mui/icons-material/History';
import Groups2Icon from '@mui/icons-material/Groups2';
import MessageIcon from '@mui/icons-material/Message';
import LocationOnIcon from "@mui/icons-material/LocationOn"
import VisibilityIcon from "@mui/icons-material/Visibility"
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FollowUpHistoryView from "../../../components/JourneyComponents/FollowUpHistoryView";
import CallFollowUpModal from "./CallFollowUpModal";
import { getPublicS3Url } from "../../../utils/s3PublicUrl";
import { message } from "antd";
import {
  followUpCategoryFilterOptions,
  normalizeFollowUpCategory,
  QUERIES_CATEGORY,
} from "./followUpCategories";
import HorizontalJourneyView from "../../../components/JourneyComponents/HorizontalJourneyView";

const queryStatuses = [
  "",
  // "Queries Received",
  "Completed",
  "Pending",
  "On Hold",
  "NA",
];

const callDirections = [
  { value: "", label: "All" },
  { value: "OUTGOING", label: "Outgoing" },
  { value: "INCOMING", label: "Incoming" },
  { value: "GROUP", label: "Group Call" },
];

const queryStatusColors = {
  // "Queries Received": { bgcolor: "#e0f2fe", color: "#0369a1" },
  Completed: { bgcolor: "#dbeafe", color: "#1d4ed8" },
  Resolved: { bgcolor: "#dcfce7", color: "#166534" },
  Pending: { bgcolor: "#fef3c7", color: "#92400e" },
  "On Hold": { bgcolor: "#ffedd5", color: "#9a3412" },
  NA: { bgcolor: "#e5e7eb", color: "#374151" },
};

const categoryColors = {
  "Meeting Touch-point": { bgcolor: "#f3e8ff", color: "#7e22ce" },
  "Program Touch-point": { bgcolor: "#dbeafe", color: "#1d4ed8" },
  [QUERIES_CATEGORY]: { bgcolor: "#fee2e2", color: "#b91c1c" },
  "Event Touch-point": { bgcolor: "#dcfce7", color: "#166534" },
  "Tour Touch-point": { bgcolor: "#fef3c7", color: "#92400e" },
};

const getSelectProps = (placeholder) => ({
  displayEmpty: true,
  renderValue: (selected) => selected || placeholder,
});

const getDirectionLabel = (value) => {
  if (value === "INCOMING") return "Incoming";
  return "Outgoing";
};

const directionChipSx = (value) => ({
  fontWeight: 800,
  bgcolor: value === "INCOMING" ? "#f3e8ff" : "#dbeafe",
  color: value === "INCOMING" ? "#7e22ce" : "#1d4ed8",
});

const getTouchPointTypeLabel = (followUp = {}) => {
  if (followUp?.callType === "GROUP") return "Group Call";
  if (followUp?.touchPointType === "IN_PERSON_MEET" || followUp?.callType === "IN_PERSON_MEET") return "In Person Meet";
  if (followUp?.touchPointType === "MESSAGE") return "Message";
  if (followUp?.callType === "INDIVIDUAL") return "Call";
  return "Call";
};

const touchPointTypeChipSx = (followUp = {}) => ({
  fontWeight: 800,
  bgcolor: followUp?.callType === "GROUP" ? "#fee2e2" : (followUp?.touchPointType === "IN_PERSON_MEET" || followUp?.callType === "IN_PERSON_MEET") ? "#fef3c7" : followUp?.touchPointType === "MESSAGE" ? "#e0f2fe" : "#dcfce7",
  color: followUp?.callType === "GROUP" ? "#b91c1c" : (followUp?.touchPointType === "IN_PERSON_MEET" || followUp?.callType === "IN_PERSON_MEET") ? "#b45309" : followUp?.touchPointType === "MESSAGE" ? "#0369a1" : "#166534",
});

const panelBorder = "1px solid rgba(126, 34, 206, 0.28)";

const StatCard = ({ icon, value, label, color = "#A92FDD" }) => (
  <Card
    sx={{
      height: 118,
      px: 3,
      py: 2,
      borderRadius: 3,
      border: panelBorder,
      boxShadow: "none",
      bgcolor: "#fff",
      display: "flex",
      alignItems: "center",
      gap: 2.5,
    }}
  >
    <Avatar sx={{ width: 62, height: 62, bgcolor: color, color: "#fff" }}>{icon}</Avatar>
    <Box>
      <Typography sx={{ fontSize: 30, lineHeight: 1, fontWeight: 950, color: "#081226" }}>
        {value}
      </Typography>
      <Typography sx={{ mt: 0.6, color: "#2d2033", fontSize: 13, fontWeight: 900, letterSpacing: 1 }}>
        {label}
      </Typography>
    </Box>
  </Card>
);

const formatDateTime = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (firstName = "", lastName = "") =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "U";

const NOTE_PREVIEW_LENGTH = 120;

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.$oid === "string") return value.$oid;
    if (value._id) return getId(value._id);
  }
  return "";
};

const getMemberId = (item) => {
  if (item?.isGroupEntry || (typeof item?._id === "string" && item._id.startsWith("group:"))) {
    return item._id;
  }
  return item?.committeeMemberId?._id || item?.committeeMemberId || "unknown";
};

const getFollowUpId = (followUp) => followUp?._id || followUp?.id || "";

const countUniqueFollowUps = (items = []) => {
  const seen = new Set();

  items.forEach((item) => {
    const followUps = item?.followUps?.length ? item.followUps : [item];

    followUps.forEach((followUp) => {
      const followUpId = getFollowUpId(followUp);
      const fallbackKey = [getMemberId(item), followUp?.dateTime, followUp?.note]
        .filter(Boolean)
        .join("|");

      seen.add(followUpId || fallbackKey);
    });
  });

  return seen.size;
};

const getDoneById = (followUp) => getId(followUp?.doneBy);

const getCreatorName = (followUp) => {
  const user = followUp?.doneBy || {};
  return [user.userFirstName, user.userLastName].filter(Boolean).join(" ") || "Me";
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const toDateTimeInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const getMemberImageUrl = (member = {}) => {
  const imageKey =
    member?.userImageURL ||
    member?.user?.userImageURL ||
    member?.userId?.userImageURL ||
    "";

  return getPublicS3Url(imageKey) || dummyImage;
};

const getParticipantDisplayName = (member = {}) =>
  member?.memberName ||
  [member?.userFirstName, member?.userLastName].filter(Boolean).join(" ") ||
  "Unknown Member";

const getFollowUpParticipants = (item = {}) => {
  const participantValues = Array.isArray(item?.participants) ? item.participants : [];
  const values = [item?.committeeMemberId, ...participantValues].filter(Boolean);
  const participants = new Map();
  const currentUserId = localStorage.getItem("userId") || "";

  values.forEach((value) => {
    const id = getId(value) || getParticipantDisplayName(value);
    if (id && id !== currentUserId && !participants.has(id)) {
      participants.set(id, value);
    }
  });

  return Array.from(participants.values());
};

const getFollowUpParticipantIds = (item) => {
  const participantIds = getFollowUpParticipants(item).map(getId).filter(Boolean);

  return participantIds.length ? participantIds : [getMemberId(item)];
};

const getParticipantMember = (item, memberId) => {
  const committeeMember = item?.committeeMemberId;
  if (getId(committeeMember) === memberId) return committeeMember;

  return (
    item?.participants?.find((participant) => getId(participant) === memberId) ||
    committeeMember
  );
};

const isGroupFollowUp = (item = {}) =>
  item?.callType === "GROUP" ||
  getFollowUpParticipants(item).length > 1;

const getGroupEntryKey = (item = {}) => {
  const label = item?.groupLabel ? item.groupLabel.trim() : "";
  if (label) {
    return `group:label:${encodeURIComponent(label.toLowerCase())}`;
  }
  const participantIds = getFollowUpParticipantIds(item);
  const sortedIds = [...new Set(participantIds)].sort().join(",");
  return `group:participants:${sortedIds}`;
};

const groupFollowUpsByMember = (items = []) => {
  const groups = new Map();

  const addGroupFollowUp = (item) => {
    const participants = getFollowUpParticipants(item);
    const primaryParticipant = participants[0] || item?.committeeMemberId;
    const groupKey = getGroupEntryKey(item);
    const itemForGroup = {
      ...item,
      committeeMemberId: primaryParticipant,
      participantMembers: participants,
      isGroupEntry: true,
    };

    const existing = groups.get(groupKey);

    if (!existing) {
      groups.set(groupKey, {
        ...itemForGroup,
        _id: groupKey,
        latestFollowUpId: getFollowUpId(item),
        lastContactedAt: item?.dateTime,
        followUpCount: 1,
        followUps: [itemForGroup],
      });
      return;
    }

    existing.followUpCount += 1;
    existing.followUps.push(itemForGroup);

    const currentLatest = new Date(existing.lastContactedAt || 0).getTime();
    const itemTime = new Date(item?.dateTime || 0).getTime();

    if (itemTime > currentLatest) {
      existing.latestFollowUpId = getFollowUpId(item);
      existing.lastContactedAt = item?.dateTime;
      existing.note = item?.note;
      existing.nextFollowUpDate = item?.nextFollowUpDate;
      existing.dateTime = item?.dateTime;
      existing.committeeMemberId = primaryParticipant;
    }
  };

  const addFollowUpToGroup = (item, memberId) => {
    const participantMember = getParticipantMember(item, memberId);
    const itemForMember = {
      ...item,
      committeeMemberId: participantMember,
    };
    const existing = groups.get(memberId);

    if (!existing) {
      groups.set(memberId, {
        ...itemForMember,
        _id: memberId,
        latestFollowUpId: item?._id,
        lastContactedAt: item?.dateTime,
        followUpCount: 1,
        followUps: [itemForMember],
      });
      return;
    }

    existing.followUpCount += 1;
    existing.followUps.push(itemForMember);

    const currentLatest = new Date(existing.lastContactedAt || 0).getTime();
    const itemTime = new Date(item?.dateTime || 0).getTime();

    if (itemTime > currentLatest) {
      existing.latestFollowUpId = item?._id;
      existing.lastContactedAt = item?.dateTime;
      existing.note = item?.note;
      existing.nextFollowUpDate = item?.nextFollowUpDate;
      existing.dateTime = item?.dateTime;
      existing.committeeMemberId = participantMember;
    }
  };

  items.forEach((item) => {
    if (Array.isArray(item?.followUps)) {
      const followUps = [...item.followUps].sort(
        (a, b) => new Date(b?.dateTime || 0) - new Date(a?.dateTime || 0)
      );
      const latestFollowUp = followUps[0] || item;
      const participants = item?.participantMembers || getFollowUpParticipants(latestFollowUp);

      groups.set(item?.isGroupEntry ? item?._id : getMemberId(item), {
        ...item,
        note: latestFollowUp?.note || item?.note,
        nextFollowUpDate:
          latestFollowUp?.nextFollowUpDate || item?.nextFollowUpDate,
        dateTime: latestFollowUp?.dateTime || item?.dateTime,
        lastContactedAt:
          item?.lastContactedAt || latestFollowUp?.dateTime || item?.dateTime,
        followUpCount: item?.followUpCount || followUps.length,
        participantMembers: participants,
        followUps,
      });
      return;
    }

    if (isGroupFollowUp(item)) {
      addGroupFollowUp(item);
      return;
    }

    getFollowUpParticipantIds(item).forEach((memberId) => {
      if (memberId) addFollowUpToGroup(item, memberId);
    });
  });

  return Array.from(groups.values()).map((item) => ({
    ...item,
    followUps: [...(item.followUps || [])].sort(
      (a, b) => new Date(b?.dateTime || 0) - new Date(a?.dateTime || 0)
    ),
  }));
};

const invertCallDirection = (value) => {
  if (value === "OUTGOING") return "INCOMING";
  if (value === "INCOMING") return "OUTGOING";
  return value;
};

const getMemberPerspectiveCallDirection = (followUp, memberId) => {
  const baseDirection = followUp?.sourceCallDirection || followUp?.callDirection;
  if (!memberId || !getFollowUpParticipantIds(followUp).includes(memberId)) {
    return baseDirection;
  }

  if (getDoneById(followUp) === memberId) {
    return baseDirection;
  }

  return invertCallDirection(baseDirection);
};

const getMemberHistoryFromFollowUps = (selectedFollowUp, items = []) => {
  if (!selectedFollowUp) return [];

  if (selectedFollowUp?.isGroupEntry) {
    return selectedFollowUp?.followUps || [];
  }

  const memberId = getId(selectedFollowUp?.committeeMemberId) || getMemberId(selectedFollowUp);

  if (!memberId || memberId === "unknown") {
    return selectedFollowUp?.followUps || [];
  }

  const seen = new Set();
  const matchedFollowUps = [];

  items.forEach((item) => {
    const followUps = Array.isArray(item?.followUps) ? item.followUps : [item];

    followUps.forEach((followUp) => {
      const participantIds = getFollowUpParticipantIds(followUp);

      if (!participantIds.includes(memberId)) return;

      const followUpId = getFollowUpId(followUp);
      const fallbackKey = [memberId, followUp?.dateTime, followUp?.note]
        .filter(Boolean)
        .join("|");
      const key = followUpId || fallbackKey;

      if (key && seen.has(key)) return;
      if (key) seen.add(key);

      matchedFollowUps.push({
        ...followUp,
        sourceCallDirection: followUp?.sourceCallDirection || followUp?.callDirection,
        callDirection: followUp?.callDirection,
        committeeMemberId: getParticipantMember(followUp, memberId),
        participantMembers: getFollowUpParticipants(followUp),
      });
    });
  });

  return matchedFollowUps.length ? matchedFollowUps : selectedFollowUp?.followUps || [];
};

const FollowUpPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    category: "",
    queryStatus: "",
    callDirection: "",
  });
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [editingFollowUpId, setEditingFollowUpId] = useState("");
  const [editForm, setEditForm] = useState({
    note: "",
    dateTime: "",
    nextFollowUpDate: "",
    callDirection: "OUTGOING",
    category: "Meeting Touch-point",
    queryStatus: "NA",
  });
  const [savingFollowUpId, setSavingFollowUpId] = useState("");
  const [deletingFollowUpId, setDeletingFollowUpId] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTouchpoint, setEditingTouchpoint] = useState(null);
  const currentUserId = useMemo(() => localStorage.getItem("userId") || "", []);
  const [expandedIndex, setExpandedIndex] = useState()

  const navigate = useNavigate()
  const theme = useTheme()

  const canManageFollowUp = useCallback(
    (followUp) => Boolean(currentUserId) && getDoneById(followUp) === currentUserId,
    [currentUserId]
  );

  const fetchFollowUps = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [myRes, incomingRes] = await Promise.all([
        ApiCallService.get(`${process.env.REACT_APP_BASE_URL}/followUp`, {}),
        ApiCallService.get(`${process.env.REACT_APP_BASE_URL}/followUp/incoming`),
      ]);

      const myData = myRes?.data || [];
      const incomingData = incomingRes?.data || [];

      // Merge and deduplicate by _id
      const seen = new Set();
      const merged = [];

      // Add my outgoing touch-points first
      myData.forEach((item) => {
        const id = item._id?.toString?.() || item._id;
        if (id && !seen.has(id)) {
          seen.add(id);
          merged.push(item);
        }
      });

      // Add incoming touch-points, swapping committeeMemberId with doneBy
      // so cards show WHO called (the caller/initiator) instead of the current user
      const userId = localStorage.getItem("userId") || "";
      incomingData.forEach((item) => {
        const id = item._id?.toString?.() || item._id;
        if (id && !seen.has(id)) {
          seen.add(id);
          const doneByValue = item.doneBy?._id || item.doneBy;
          if (userId && doneByValue !== userId && item.doneBy) {
            // I didn't create this touch-point — show the creator's name on the card
            merged.push({ ...item, committeeMemberId: item.doneBy });
          } else {
            merged.push(item);
          }
        }
      });

      setData(merged);
      return merged;
    } catch (err) {
      console.error(err);
      setError("Failed to load touch-points");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFollowUps();
  }, [fetchFollowUps]);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const clearFilters = () => {
    setSearch("");
    setFilters({
      category: "",
      queryStatus: "",
      callDirection: "",
    });
    setSelectedFollowUp(null);
    setExpandedNotes({});
  };

  const handleCloseHistory = () => {
    setSelectedFollowUp(null);
    setExpandedNotes({});
    setEditingFollowUpId("");
  };

  const refreshSelectedMember = useCallback(
    async (memberId) => {
      const nextData = await fetchFollowUps();
      const nextSelected = groupFollowUpsByMember(nextData).find(
        (item) => getMemberId(item) === memberId
      );

      setSelectedFollowUp(nextSelected || null);
      setExpandedNotes({});
    },
    [fetchFollowUps]
  );

  const handleStartEdit = (followUp) => {
    if (!canManageFollowUp(followUp)) {
      message.warning("You can edit only touch-points created by you");
      return;
    }

    setEditingTouchpoint(followUp);
    setEditModalOpen(true);
  };

  const handleSaveEditSuccess = async () => {
    const nextData = await fetchFollowUps();
    if (selectedFollowUp) {
      const memberId = getMemberId(selectedFollowUp);
      const nextSelected = groupFollowUpsByMember(nextData).find(
        (item) => getMemberId(item) === memberId
      );
      setSelectedFollowUp(nextSelected || null);
    }
    setEditModalOpen(false);
    setEditingTouchpoint(null);
  };

  const handleCancelEdit = () => {
    setEditingFollowUpId("");
    setEditForm({
      note: "",
      dateTime: "",
      nextFollowUpDate: "",
      callDirection: "OUTGOING",
      category: "Meeting Touch-point",
      queryStatus: "NA",
    });
  };

  const handleSaveEdit = async (followUp) => {
    if (!canManageFollowUp(followUp)) {
      message.warning("You can edit only touch-points created by you");
      return;
    }

    const followUpId = getFollowUpId(followUp);

    if (!editForm.note.trim()) {
      message.warning("Please enter conversation notes");
      return;
    }

    if (!editForm.dateTime) {
      message.warning("Please select call date and time");
      return;
    }

    const memberId = getMemberId(selectedFollowUp);
    const payload = {
      note: editForm.note.trim(),
      dateTime: new Date(editForm.dateTime).toISOString(),
      nextFollowUpDate: editForm.nextFollowUpDate || null,
      callDirection: editForm.callDirection,
      category: editForm.category,
      queryStatus: editForm.queryStatus,
    };

    try {
      setSavingFollowUpId(followUpId);

      await ApiCallService.put(
        `${process.env.REACT_APP_BASE_URL}/followUp/${followUpId}`,
        payload
      );

      message.success("Touch-point updated successfully");
      handleCancelEdit();
      await refreshSelectedMember(memberId);
    } catch (err) {
      console.error("Failed to update touch-point", err);
      message.error(
        err?.response?.data?.message || "Failed to update touch-point"
      );
    } finally {
      setSavingFollowUpId("");
    }
  };

  const handleDeleteFollowUp = async (followUp) => {
    if (!canManageFollowUp(followUp)) {
      message.warning("You can delete only touch-points created by you");
      return;
    }

    const followUpId = getFollowUpId(followUp);
    const confirmed = window.confirm(
      "Delete this touch-point? This will move it to inactive touch-points."
    );

    if (!confirmed) return;

    const memberId = getMemberId(selectedFollowUp);

    try {
      setDeletingFollowUpId(followUpId);

      await ApiCallService.delete(
        `${process.env.REACT_APP_BASE_URL}/followUp/${followUpId}`
      );

      message.success("Touch-point deleted successfully");
      handleCancelEdit();
      await refreshSelectedMember(memberId);
    } catch (err) {
      console.error("Failed to delete touch-point", err);
      message.error(
        err?.response?.data?.message || "Failed to delete touch-point"
      );
    } finally {
      setDeletingFollowUpId("");
    }
  };

  const groupedData = useMemo(() => groupFollowUpsByMember(data), [data]);

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();

    return groupedData.reduce((acc, item) => {
      const followUps = item?.followUps || [];
      const matchingFollowUps = followUps.filter((followUp) => {
        const categoryMatch =
          !filters.category ||
          normalizeFollowUpCategory(followUp?.category) === filters.category;
        const statusMatch = !filters.queryStatus || followUp?.queryStatus === filters.queryStatus;
        const directionMatch = !filters.callDirection || followUp?.callDirection === filters.callDirection;
        return categoryMatch && statusMatch && directionMatch;
      });

      if (matchingFollowUps.length === 0) return acc;

      const member = item?.committeeMemberId;

      const text = [
        member?.userFirstName,
        member?.userLastName,
        ...(item?.participantMembers || []).flatMap((participant) => [
          participant?.userFirstName,
          participant?.userLastName,
          participant?.memberName,
        ]),
        item?.note,
        item?.nextFollowUpDate,
        item?.dateTime,
        item?.lastContactedAt,
        item?.category,
        item?.queryStatus,
        item?.callType,
        item?.touchPointType,
        item?.callDirection,
        item?.doneBy?.userFirstName,
        item?.doneBy?.userLastName,
        ...matchingFollowUps.flatMap((followUp) => [
          followUp?.note,
          followUp?.category,
          followUp?.queryStatus,
          followUp?.callType,
          followUp?.touchPointType,
          followUp?.callDirection,
          followUp?.dateTime,
          followUp?.nextFollowUpDate,
          followUp?.doneBy?.userFirstName,
          followUp?.doneBy?.userLastName,
          ...getFollowUpParticipants(followUp).flatMap((participant) => [
            participant?.userFirstName,
            participant?.userLastName,
            participant?.memberName,
          ]),
        ]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (q && !text.includes(q)) return acc;

      acc.push({
        ...item,
        note: matchingFollowUps[0]?.note || item?.note,
        nextFollowUpDate: matchingFollowUps[0]?.nextFollowUpDate || item?.nextFollowUpDate,
        dateTime: matchingFollowUps[0]?.dateTime || item?.dateTime,
        lastContactedAt: matchingFollowUps[0]?.dateTime || item?.lastContactedAt,
        followUpCount: matchingFollowUps.length,
        followUps: matchingFollowUps,
      });
      return acc;
    }, []);
  }, [groupedData, search, filters]);

  const totalFollowUps = countUniqueFollowUps(groupedData);
  const filteredFollowUps = countUniqueFollowUps(filteredData);

  const selectedMember = selectedFollowUp?.committeeMemberId || {};
  const selectedMemberName =
    `${selectedMember?.userFirstName || ""} ${selectedMember?.userLastName || ""
      }`.trim() || "Unknown Member";
  const selectedParticipantMembers = selectedFollowUp?.participantMembers?.length
    ? selectedFollowUp.participantMembers
    : getFollowUpParticipants(selectedFollowUp);
  const selectedExtraParticipantCount = Math.max(selectedParticipantMembers.length - 1, 0);
  const selectedExtraParticipantNames = selectedParticipantMembers
    .slice(1)
    .map(getParticipantDisplayName)
    .join(", ");
  const selectedHistory = useMemo(
    () => getMemberHistoryFromFollowUps(selectedFollowUp, data),
    [selectedFollowUp, data]
  );

  const journeyEntries = useMemo(() => {
    return selectedHistory.map((item) => ({
      _id: getFollowUpId(item),

      startDate: item.dateTime,
      endDate: item.nextFollowUpDate,
      position: getTouchPointTypeLabel(item),
      description: item.note || "No notes available.",

      meta: (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1 }}
        >
          <Chip
            size="small"
            label={normalizeFollowUpCategory(item.category)}
            sx={{
              fontWeight: 700,
              ...(categoryColors[
                normalizeFollowUpCategory(item.category)
              ] || categoryColors["Meeting Touch-point"]),
            }}
          />

          <Chip
            size="small"
            label={item.queryStatus || "NA"}
            sx={{
              fontWeight: 700,
              ...(queryStatusColors[item.queryStatus] || queryStatusColors.NA),
            }}
          />
        </Stack>
      ),

      original: item,
    }));
  }, [selectedHistory]);

  const touchPointIcons = {
    Call: <CallMadeIcon />,
    // Meeting: <GroupsIcon />,
    // Email: <EmailIcon />,
    // WhatsApp: <WhatsAppIcon />,
    // Visit: <LocationOnIcon />,
    // default: <HistoryIcon />,
  };

  const getJourneyIcon = (type) => {
    if (type === "Call") {
      return data?.callDirection === "INCOMING"
        ? <CallReceivedIcon />
        : <CallMadeIcon />;
    }

    switch (type) {
      case "Meeting":
        return <Groups2Icon />;
      case "Message":
        return <MessageIcon />;
      // case "Email":
      //     return <EmailIcon />;
      case "Meeting":
        return <LocationOnIcon />;
      default:
        return <HistoryIcon />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.accent.followUpUltraWhite,
        color: "#081226",
      }}
    >
      <Box sx={{ mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{display:"flex", flexDirection:"row", gap:"5px"}}>
              <Button
              variant="text"
                // startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/callFollowUps")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius:"9999px",
                  width:"fit-content",
                  color:theme.palette.accent.followUp,
                  "&:hover":{
                    bgcolor:theme.palette.accent.followUp
                  }
                }}
              >
                <ArrowBackIcon/>
              </Button>
              <Typography
                variant="h4"
                sx={{
                  color: "#07111f",
                  fontSize: { xs: 20, sm:24, md: 24 },
                  lineHeight: 1.1,
                  fontWeight: 900,
                }}
              >
                My Touch-points
              </Typography>
            </Box>
          
            <Typography
              sx={{
                mt: 0.5,
                color: "#7a6b63",
                fontSize: 15,
              }}
            >
              Manage and track your active communication pipelines.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, sm: 3 },
              ml: "auto",
            }}
          >

            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ mt: 0.4, color: "#7a6b63", fontSize: 11, fontWeight: 900, letterSpacing: 1 }}>
                People contacted
              </Typography>
              <Typography sx={{ color: "#07111f", fontSize: 22, lineHeight: 1, fontWeight: 900 }}>
                {filteredData.length}
              </Typography>
            </Box>
            <Box sx={{ width: "1px", height: 34, bgcolor: "rgba(126, 34, 206, 0.25)" }} />
            <Box sx={{ textAlign: "right" }}>

              <Typography sx={{ mt: 0.4, color: "#7a6b63", fontSize: 11, fontWeight: 900, letterSpacing: 1 }}>
                Touch Points
              </Typography>
              <Typography sx={{ color: "#07111f", fontSize: 22, lineHeight: 1, fontWeight: 900 }}>
                {filteredFollowUps}
              </Typography>
            </Box>
          </Box>
        </Box>



        {/* </Grid> */}

        <Card
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "#fff",
            borderRadius: 1,
            border: panelBorder,
            boxShadow: "none",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 52,
                    borderRadius: 2,
                    bgcolor: theme.palette.accent.followUpUltraWhite,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField select fullWidth size="small" value={filters.category} onChange={handleFilterChange("category")} SelectProps={getSelectProps("Category")}>
                {followUpCategoryFilterOptions.map((item) => (
                  <MenuItem key={item || "all"} value={item}>
                    {item || "Category"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField select fullWidth size="small" value={filters.queryStatus} onChange={handleFilterChange("queryStatus")} SelectProps={getSelectProps("Status")}>
                {queryStatuses.map((item) => (
                  <MenuItem key={item || "all"} value={item}>
                    {item || "Status"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={1.7}>
              <TextField select fullWidth size="small" value={filters.callDirection} onChange={handleFilterChange("callDirection")} SelectProps={getSelectProps("Call Type")}>
                {callDirections.map((item) => (
                  <MenuItem key={item.value || "all"} value={item.value}>
                    {item.value ? item.label : "Call Type"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={1.3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={clearFilters}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: theme.palette.accent.followUp,
                  color: theme.palette.accent.followUp,
                  "&:hover" : {
                    bgcolor:theme.palette.accent.followUp
                  }
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* Loading */}
        {loading && (
          <Box textAlign="center" mt={8}>
            <CircularProgress thickness={4} />
            <Typography mt={2} color="text.secondary">
              Loading Touch-points...
            </Typography>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {/* Empty */}
        {!loading && filteredData.length === 0 && (
          <Card
            sx={{
              p: 5,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed #cbd5e1",
              bgcolor: "#fff",
            }}
          >
            <EventNoteIcon sx={{ fontSize: 54, color: "#90a4ae", mb: 1 }} />
            <Typography variant="h6" fontWeight={700}>
              No Touch-points found
            </Typography>
            <Typography color="text.secondary">
              Try changing your search or add a new follow-up.
            </Typography>
          </Card>
        )}

        {/* Data */}
        {!loading && filteredData.length > 0 && (
          <Stack spacing={1.4}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {filteredData.map((item) => {
                const member = item?.committeeMemberId || {};
                const memberImageUrl = getMemberImageUrl(member);
                const fullName =
                  `${member?.userFirstName || ""} ${member?.userLastName || ""}`.trim() ||
                  "Unknown Member";
                const latestFollowUp = item?.followUps?.[0] || item;
                const latestCategory = normalizeFollowUpCategory(latestFollowUp?.category) || "Meeting Touch-point";
                const latestQueryStatus = latestFollowUp?.queryStatus || "NA";
                const latestDirection = latestFollowUp?.callDirection || "OUTGOING";
                const latestCallType = getTouchPointTypeLabel(latestFollowUp);
                const latestNote = latestFollowUp?.note || "No notes added.";
                const followUpCount = item?.followUps?.length || item?.followUpCount || 1;
                const participantMembers = item?.participantMembers?.length
                  ? item.participantMembers
                  : getFollowUpParticipants(latestFollowUp);
                const extraParticipantCount = Math.max(participantMembers.length - 1, 0);
                const extraParticipantNames = participantMembers
                  .slice(1)
                  .map(getParticipantDisplayName)
                  .join(", ");

                // need to calc history here : 
                const itemHistory = getMemberHistoryFromFollowUps(item, data);
                const totalTouchPoints = itemHistory.length;
                const groupTouchPoints = itemHistory.filter((entry) => entry?.callType === "GROUP").length;
                const directTouchPoints = itemHistory.filter((entry) => entry?.callType !== "GROUP").length;

                return (
                  <Card
                    key={item._id}
                    sx={{
                      p: 2.2,
                      borderRadius: 2,
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: "0 2px 10px rgba(15,23,42,0.05)",
                      transition: "all .2s ease",

                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 24px rgba(15,23,42,.08)",
                      },
                    }}
                  >
                    <Stack spacing={2}>

                      {/* Top */}
                      <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Stack direction="row" spacing={2}>
                          <Avatar
                            src={item?.callType === "GROUP" ? <><Groups2Icon sx={{ fontSize: "36px" }} /></> : memberImageUrl}
                            sx={{
                              width: 62,
                              height: 62,
                              flexShrink: 0,
                            }}
                          >
                            {item?.callType === "GROUP" ? <><Groups2Icon sx={{ fontSize: "36px" }} /></> :
                              getInitials(member?.userFirstName, member?.userLastName)}
                          </Avatar>

                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "18px !important",
                                lineHeight: 1.2,
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" }
                              }}
                            >
                              {fullName}

                              {item.callType === "GROUP" &&
                                item.participants?.length > 1 && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      ml: 0.5,
                                      fontSize: "14px !important",
                                      fontWeight: 600,
                                      color: "text.secondary",
                                    }}
                                  >
                                    + {item.participants.length - 1} others
                                  </Typography>
                                )}
                            </Typography>

                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                              <Typography
                                sx={{
                                  mt: 0.3,
                                  color: "text.secondary",
                                  fontSize: 14,
                                }}
                              >
                                <PermPhoneMsgIcon sx={{fontSize:"20px"}}/> - {formatDate(item?.dateTime)}
                              </Typography>
                              {/* <span style={{ marginRight: "10px", marginLeft: "10px", fontWeight: 500 }}>|</span> */}
                              <Stack
                                direction="row"
                                spacing={1}
                                color={theme.palette.accent.followUpLight}
                                alignItems="center"
                                sx={{ fontSize: 16 }}
                              >
                                {latestCallType === "Message" ? (
                                  <MessageIcon fontSize="small" />
                                ) : latestCallType === "Group Call" ? (
                                  <Groups2Icon fontSize="small" />
                                ) : latestCallType === "Call" ? (
                                  <CallMadeIcon fontSize="small" />
                                ) : null}

                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: 16,
                                    color:theme.palette.accent.followUpLight
                                  }}
                                >
                                  {/* {getDirectionLabel(latestDirection)} */}
                                  {latestCallType}
                                </Typography>
                              </Stack>
                            </Box>
                          </Box>
                        </Stack>

                        <Tooltip title={`View touch-point${followUpCount > 1 ? "s" : ""}`} arrow placement="bottom">
                          <IconButton
                            onClick={() => setSelectedFollowUp(item)}
                            sx={{
                              height: "100%",
                              color: "#9238de",
                              // border: "1px solid",
                              // borderRadius: "8px",
                              // borderColor: theme.palette.secondary.figma,
                            }}
                          >
                            <VisibilityIcon sx={{ 
                              fontSize: "24px",
                              color:theme.palette.accent.followUpLight
                             }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      <Box textAlign="right">
                  
                          <Typography sx={{fontSize: "20px !important"}}>
                            <b>{String(totalTouchPoints).padStart(2, "0")}</b>
                          </Typography>

                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: "16px !important",
                          }}
                        >
                          touch-point{followUpCount > 1 ? "s" : ""}
                        </Typography>
                      </Box>

                      {/* Bottom Bar */}
                      {/* <Box
                        sx={(theme) => ({
                          bgcolor: "transparent",
                          borderRadius: 2,
                          color: "#4e4e4e",

                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",

                          overflow: "hidden",
                        })}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 1 }}
                        >
                          <Chip
                            size="small"
                            label={latestCategory}
                            sx={{
                              fontWeight: 700,
                              ...(categoryColors[latestCategory] ||
                                categoryColors["Meeting Touch-point"]),
                            }}
                          />

                          <Chip
                            size="small"
                            label={latestQueryStatus}
                            sx={{
                              fontWeight: 700,
                              ...(queryStatusColors[latestQueryStatus] ||
                                queryStatusColors.NA),
                            }}
                          />
                        </Stack>
                      </Box> */}
                    </Stack>
                  </Card>
                );
              })}
            </Box>
          </Stack>
        )}

        <Dialog
          open={Boolean(selectedFollowUp)}
          onClose={handleCloseHistory}
          fullWidth
          // maxWidth={"md"}
          PaperProps={{
            sx: {
              width: {
                xs: "98%",
                md: "1100px",
                lg: "1250px",
              },
              maxWidth: "1250px",
              height: "80vh",
              borderRadius: 4,
              overflow: "hidden",
              bgcolor: "#F8FAFC",
              boxShadow: "0 25px 70px rgba(15,23,42,.25)",
            },
          }}
          BackdropProps={{
            sx: {
              backdropFilter: "blur(2px)",
              backgroundColor: "rgba(15,23,42,0.45)",
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              bgcolor: "#fff",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Box
              sx={{
                px: 3,
                position: "relative",
              }}
            >
              <IconButton
                onClick={handleCloseHistory}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: 0,
                  width: 36,
                  height: 36,
                  border: "1px solid #E5E7EB",
                  bgcolor: "#fff",
                  "&:hover": {
                    bgcolor: "#F3F4F6",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <Avatar
                  src={selectedFollowUp?.callType === "GROUP" ? <><Groups2Icon /></> : getMemberImageUrl(selectedMember)}
                  alt={selectedFollowUp?.callType === "GROUP" ? "Group Call" : selectedMemberName}
                  sx={{
                    width: 80,
                    height: 80,
                    border: "1px gray solid"
                  }}
                >
                  {selectedFollowUp?.callType === "GROUP" ? <><Groups2Icon sx={{ fontSize: "52px", color:theme.palette.accent.followUpUltraWhite }} /></> :
                    getInitials(
                      selectedMember?.userFirstName,
                      selectedMember?.userLastName
                    )}
                </Avatar>

                <Box flex={1}>
                  {selectedFollowUp?.callType === "GROUP" &&
                    selectedParticipantMembers.length > 0 ? (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        useFlexGap
                        flexWrap="wrap"
                        alignItems="center"
                        sx={{
                          mt: 1,
                          mx: 1.25,
                          maxHeight: "100px",
                          overflowY: "auto",
                          overflowX: "10px",
                          pr: 1,

                          scrollbarWidth: "thin",
                          scrollbarColor: "#c4b5fd transparent",

                          "&::-webkit-scrollbar": {
                            width: "5px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "transparent",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#c4b5fd",
                            borderRadius: "10px",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "#a78bfa",
                          },

                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "text.secondary",
                          }}
                        >
                          {selectedFollowUp?.callType === "GROUP"
                            ? "Participants:"
                            : "Participant:"}
                        </Typography>                            
                        {selectedParticipantMembers.map((participant, index) => (
                          <Chip
                            key={getId(participant) || index}
                            size="small"
                            label={getParticipantDisplayName(participant)}
                            avatar={
                              <Avatar
                                src={getMemberImageUrl(participant)}
                                alt={getParticipantDisplayName(participant)}
                              />
                            }
                            sx={{
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography sx={{
                            fontSize: "18px !important",
                            fontWeight: 600,
                            color: "text.secondary",
                          }}>
                        {(selectedMemberName)}
                      </Typography>
                    )}

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: .5 }}
                  >
                    <EventNoteIcon
                      sx={{
                        fontSize: 17,
                        color: "text.secondary",
                      }}
                    />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Total Touch-points :
                      <Box
                        component="span"
                        sx={{
                          ml: .5,
                          fontWeight: 700,
                          color: theme.palette.accent.followUpLight,
                          fontSize: 17,
                        }}
                      >
                        {String(selectedHistory.length).padStart(2, "0")}
                      </Box>
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </DialogTitle>

          <DialogContent
            sx={{
              // p: 2,
              bgcolor: "#F8FAFC",
              overflow: "scroll",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* <HorizontalJourneyView
              entries={journeyEntries}
              onSelect={(entry) => handleStartEdit(entry.original)}
              formatDateForInput={formatDate}
              getJourneyIcon={getJourneyIcon}
              expandedIndex={expandedIndex}
              setExpandedIndex={setExpandedIndex}
              maxLength={180}
            /> */}
            <FollowUpHistoryView
              entries={selectedHistory}
              onEdit={(entry) => handleStartEdit(entry)}
              getTouchPointTypeLabel={getTouchPointTypeLabel}
              normalizeFollowUpCategory={normalizeFollowUpCategory}
              categoryColors={categoryColors}
              queryStatusColors={queryStatusColors}
            />
          </DialogContent>
        </Dialog>

        {editModalOpen && (
          <CallFollowUpModal
            open={editModalOpen}
            onClose={() => {
              setEditModalOpen(false);
              setEditingTouchpoint(null);
            }}
            record={editingTouchpoint}
            member={selectedMember}
            onSaved={handleSaveEditSuccess}
          />
        )}
      </Box>
    </Box>
  );
};

export default FollowUpPage;